import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarRange,
  CheckCircle2,
  Clock3,
  Info,
  Minus,
  Paperclip,
  Pencil,
  Plus,
  RefreshCcw,
  Users,
  Zap,
} from 'lucide-react';
import toast from 'react-hot-toast';
import AttachmentGallery from '../components/crm/AttachmentGallery';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabase';
import { getCrmErrorMessage } from '../utils/crmErrors';
import {
  calculateMinutesFromTimes,
  fetchCrmMeetingDetail,
  fetchCrmMeetingOptions,
  finalizeCrmMeeting,
  formatMinutes,
  getMeetingStatusMeta,
  isProcessedMeetingStatus,
  saveCrmMeetingForFinalize,
} from '../utils/crmWorkflow';
import CrmFieldLabel from '../components/crm/CrmFieldLabel';

/* ─── Tooltips ─────────────────────────────────────────────────────────────── */

const MEETING_DETAIL_TOOLTIPS = {
  start_time: 'Hora real de inicio que se usara para recalcular la duracion total antes de procesar la reunion.',
  end_time: 'Hora real de cierre de la reunion. Debe ser posterior al inicio.',
  total: 'Duracion total que se va a repartir entre los temas tratados.',
  treated: 'Marca si el tema efectivamente se hablo. Solo los temas tratados generan imputacion de horas.',
  topic_minutes: 'Minutos reales del tema. Si lo dejas vacio, el sistema usa el tiempo restante de la reunion.',
  topic_participants: 'Participantes exclusivos de este tema. Si no seleccionas nadie, aplica la reunion general.',
};

/* ─── Color palette para barra visual ──────────────────────────────────────── */

const TOPIC_COLORS = [
  '#51A7FD', '#3FF2AA', '#F59E0B', '#EC4899', '#8B5CF6',
  '#06B6D4', '#10B981', '#F97316', '#EF4444', '#6366F1',
];

/* ─── Badge ─────────────────────────────────────────────────────────────────── */

function Badge({ label, tone = 'slate' }) {
  const tones = {
    slate: 'bg-slate-100 border-slate-200 text-slate-600',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
  };
  return (
    <span className={`inline-flex items-center rounded-xl border px-2.5 py-1 text-[8px] font-black uppercase tracking-widest ${tones[tone] || tones.slate}`}>
      {label}
    </span>
  );
}

/* ─── Panel header ──────────────────────────────────────────────────────────── */

function PanelTitle({ icon: Icon, title, subtitle, right }) {
  return (
    <div className="bg-[#0B2A36] px-4 py-3 border-b border-white/10 flex items-center gap-3 rounded-t-2xl">
      <Icon size={16} className="text-[#3FF2AA]" />
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-black uppercase tracking-widest text-white truncate">{title}</p>
        {subtitle ? <p className="text-[9px] font-bold uppercase tracking-widest text-white/50">{subtitle}</p> : null}
      </div>
      {right && <div className="shrink-0">{right}</div>}
    </div>
  );
}

/* ─── KPI Card ──────────────────────────────────────────────────────────────── */

function KpiCard({ label, value, sub, highlight }) {
  return (
    <div className={`rounded-2xl border shadow-sm p-4 ${highlight === 'green' ? 'bg-emerald-50 border-emerald-200' : highlight === 'amber' ? 'bg-amber-50 border-amber-200' : highlight === 'rose' ? 'bg-rose-50 border-rose-200' : 'bg-white border-slate-200'}`}>
      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">{label}</p>
      <p className={`mt-1.5 text-2xl font-black tabular-nums ${highlight === 'green' ? 'text-emerald-600' : highlight === 'amber' ? 'text-amber-600' : highlight === 'rose' ? 'text-rose-600' : 'text-slate-800'}`}>{value}</p>
      {sub && <p className="text-[10px] font-bold text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

/* ─── Helpers ───────────────────────────────────────────────────────────────── */

function normalizeInputTime(value) {
  return String(value || '').slice(0, 5);
}

function addMinutesToTime(timeValue, minutesToAdd) {
  const normalizedTime = normalizeInputTime(timeValue);
  const [hours, minutes] = normalizedTime.split(':').map((v) => Number(v || 0));
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return '10:00';
  const baseMinutes = hours * 60 + minutes;
  const totalMinutes = baseMinutes + Math.max(Number(minutesToAdd) || 0, 0);
  const nextHours = Math.floor(totalMinutes / 60) % 24;
  const nextMinutes = totalMinutes % 60;
  return `${String(nextHours).padStart(2, '0')}:${String(nextMinutes).padStart(2, '0')}`;
}

function buildMeetingDraft(meeting) {
  return {
    start_time: normalizeInputTime(meeting?.start_time || '09:00'),
    end_time: normalizeInputTime(
      meeting?.end_time ||
        addMinutesToTime(meeting?.start_time || '09:00', meeting?.duration_minutes || 60),
    ),
    topics: (meeting?.topics || []).map((topic) => ({
      id: topic.id,
      project_id: topic.project_id || '',
      is_treated: Boolean(topic.is_treated),
      topic_minutes: topic.topic_minutes == null ? '' : String(topic.topic_minutes),
      topic_participant_ids: [
        ...new Set(
          (topic.topic_participants || []).map((row) => row.profile_id).filter(Boolean),
        ),
      ],
    })),
  };
}

function validateMeetingDraft(meeting, draft) {
  if (!draft?.start_time) return 'Completa la hora de inicio antes de finalizar la reunion';
  if (!draft?.end_time) return 'Completa la hora de finalizacion antes de finalizar la reunion';

  const totalMinutes = calculateMinutesFromTimes(draft.start_time, draft.end_time);
  if (!totalMinutes) return 'La hora de finalizacion debe ser posterior al inicio';

  const topics = draft.topics || [];
  if (!topics.length) return 'La reunion no tiene temas para imputar';

  const treatedTopics = topics.filter((topic) => topic.is_treated);
  if (!treatedTopics.length) return 'Marca al menos un tema como tratado antes de finalizar la reunion';

  let explicitMinutes = 0;

  for (const topicDraft of topics) {
    const topicRow = (meeting.topics || []).find((row) => row.id === topicDraft.id);
    const extraParticipants = topicDraft.topic_participant_ids || [];
    const numericTopicMinutes =
      topicDraft.topic_minutes === '' ? null : Number(topicDraft.topic_minutes || 0);
    const linkedProjectId = topicDraft.project_id || topicRow?.project_id || null;
    const linkedTicketId = topicRow?.ticket_id || null;

    if (!linkedProjectId && !linkedTicketId && topicDraft.is_treated) {
      return 'Todos los temas tratados deben estar asociados a un proyecto';
    }

    if (numericTopicMinutes != null && numericTopicMinutes <= 0) {
      return 'El tiempo de tema debe ser mayor a cero';
    }

    if (
      extraParticipants.length &&
      (numericTopicMinutes == null || Number.isNaN(numericTopicMinutes))
    ) {
      return 'Si agregas participantes solo a un tema, debes completar el tiempo de ese tema';
    }

    if (
      topicDraft.is_treated &&
      numericTopicMinutes != null &&
      !Number.isNaN(numericTopicMinutes)
    ) {
      explicitMinutes += numericTopicMinutes;
    }
  }

  if (explicitMinutes > totalMinutes) {
    return 'La suma de tiempos de tema no puede superar la duracion total de la reunion';
  }

  return null;
}

/* ─── Componente principal ──────────────────────────────────────────────────── */

export default function AgendaDetalle() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { id: meetingId } = useParams();

  const [options, setOptions] = useState({ clients: [], profiles: [], tickets: [], projects: [] });
  const [meeting, setMeeting] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);

  /* ── Load ─────────────────────────────────────────────────────────────────── */

  const loadMeeting = useCallback(async () => {
    if (!profile?.organization_id || !meetingId) return;
    setLoading(true);
    try {
      const [meetingOptions, meetingRow] = await Promise.all([
        fetchCrmMeetingOptions(profile.organization_id),
        fetchCrmMeetingDetail(profile.organization_id, meetingId),
      ]);
      setOptions(meetingOptions);
      setMeeting(meetingRow);
      setDraft(meetingRow ? buildMeetingDraft(meetingRow) : null);
    } catch (error) {
      console.error(error);
      toast.error(getCrmErrorMessage(error, 'No se pudo cargar el detalle de la reunion'), {
        id: 'crm-meeting-detail-load-error',
      });
    } finally {
      setLoading(false);
    }
  }, [meetingId, profile?.organization_id]);

  useEffect(() => { loadMeeting(); }, [loadMeeting]);

  useEffect(() => {
    if (!profile?.organization_id || !meetingId) return;
    const channel = supabase
      .channel(`crm-meeting-detail-${meetingId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_meetings', filter: `organization_id=eq.${profile.organization_id}` }, loadMeeting)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_meeting_participants', filter: `organization_id=eq.${profile.organization_id}` }, loadMeeting)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_meeting_topics', filter: `organization_id=eq.${profile.organization_id}` }, loadMeeting)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_meeting_topic_participants', filter: `organization_id=eq.${profile.organization_id}` }, loadMeeting)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [loadMeeting, meetingId, profile?.organization_id]);

  /* ── Maps ─────────────────────────────────────────────────────────────────── */

  const clientMap = useMemo(
    () => Object.fromEntries((options.clients || []).map((row) => [row.id, row])),
    [options.clients],
  );
  const userMap = useMemo(
    () => Object.fromEntries((options.profiles || []).map((row) => [row.id, row])),
    [options.profiles],
  );
  const ticketMap = useMemo(
    () => Object.fromEntries((options.tickets || []).map((row) => [row.id, row])),
    [options.tickets],
  );
  const projectMap = useMemo(
    () => Object.fromEntries((options.projects || []).map((row) => [row.id, row])),
    [options.projects],
  );

  /* ── Draft handlers ───────────────────────────────────────────────────────── */

  const handleMeetingDraftChange = (field, value) => {
    setDraft((current) => ({ ...(current || {}), [field]: value }));
  };

  const handleTopicDraftChange = (topicId, field, value) => {
    setDraft((current) => ({
      ...(current || {}),
      topics: (current?.topics || []).map((topic) =>
        topic.id === topicId ? { ...topic, [field]: value } : topic,
      ),
    }));
  };

  const handleToggleTopicParticipant = (topicId, profileId) => {
    setDraft((current) => ({
      ...(current || {}),
      topics: (current?.topics || []).map((topic) => {
        if (topic.id !== topicId) return topic;
        const nextIds = new Set(topic.topic_participant_ids || []);
        if (nextIds.has(profileId)) nextIds.delete(profileId);
        else nextIds.add(profileId);
        return { ...topic, topic_participant_ids: Array.from(nextIds) };
      }),
    }));
  };

  /* ── Auto-balance ─────────────────────────────────────────────────────────── */

  const autoBalance = () => {
    if (!draft) return;
    const treatedTopics = draft.topics.filter((t) => t.is_treated);
    if (!treatedTopics.length) return;
    const target = scheduledMinutes;
    const each = Math.floor(target / treatedTopics.length);
    let leftover = target - each * treatedTopics.length;
    setDraft((current) => ({
      ...current,
      topics: current.topics.map((t) => {
        if (!t.is_treated) return { ...t, topic_minutes: '' };
        const extra = leftover > 0 ? 1 : 0;
        if (leftover > 0) leftover--;
        return { ...t, topic_minutes: String(each + extra) };
      }),
    }));
  };

  /* ── Finalize ─────────────────────────────────────────────────────────────── */

  const handleFinalizeMeeting = async () => {
    if (!profile?.organization_id || !meeting || !draft) return;
    const validationError = validateMeetingDraft(meeting, draft);
    if (validationError) { toast.error(validationError); return; }
    setFinalizing(true);
    try {
      await saveCrmMeetingForFinalize({
        organizationId: profile.organization_id,
        meetingId: meeting.id,
        startTime: draft.start_time,
        endTime: draft.end_time,
        actorProfileId: profile.id || null,
        topics: draft.topics,
      });
      const result = await finalizeCrmMeeting(meeting.id, null);
      toast.success(`${result.time_entries_created || 0} imputaciones generadas`);
      await loadMeeting();
    } catch (error) {
      console.error(error);
      toast.error(getCrmErrorMessage(error, 'No se pudo finalizar la reunion'));
    } finally {
      setFinalizing(false);
    }
  };

  /* ── Loading / empty states ───────────────────────────────────────────────── */

  if (loading) {
    return (
      <div className="w-full px-3 py-3 sm:px-4 sm:py-4 lg:px-5 bg-[#F8FAFC] min-h-screen">
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-400">
          <p className="text-[10px] font-black uppercase tracking-widest">Cargando reunion...</p>
        </div>
      </div>
    );
  }

  if (!meeting || !draft) {
    return (
      <div className="w-full px-3 py-3 sm:px-4 sm:py-4 lg:px-5 bg-[#F8FAFC] min-h-screen">
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-center">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">No encontramos esta reunion</p>
          <button
            type="button"
            onClick={() => navigate('/sistema/agenda')}
            className="mt-4 rounded-2xl bg-[#0B2A36] px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white"
          >
            Volver a reuniones
          </button>
        </div>
      </div>
    );
  }

  /* ── Derived values ───────────────────────────────────────────────────────── */

  const scheduledMinutes = calculateMinutesFromTimes(draft.start_time, draft.end_time);
  const realMinutes =
    meeting.actual_duration_minutes ||
    calculateMinutesFromTimes(meeting.start_time, meeting.end_time) ||
    meeting.duration_minutes ||
    scheduledMinutes;
  const plannedMinutes = meeting.duration_minutes || scheduledMinutes;
  const meetingStatus = getMeetingStatusMeta(meeting.status);
  const isProcessed = isProcessedMeetingStatus(meeting.status);

  // Total minutos asignados (solo temas tratados con minutos explícitos)
  const totalAssigned = (draft.topics || [])
    .filter((t) => t.is_treated && t.topic_minutes !== '')
    .reduce((sum, t) => sum + (parseInt(t.topic_minutes, 10) || 0), 0);
  const target = isProcessed ? realMinutes : scheduledMinutes;
  const diff = target - totalAssigned;

  /* ── Render ───────────────────────────────────────────────────────────────── */

  return (
    <div className="w-full px-3 py-3 sm:px-4 sm:py-4 lg:px-5 bg-[#F8FAFC] min-h-screen animate-in fade-in duration-500">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate('/sistema/agenda')}
            className="w-10 h-10 bg-white border border-slate-200 rounded-2xl flex items-center justify-center text-slate-500 shadow-sm hover:text-[#51A7FD] transition-colors"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <CalendarRange size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge label={meetingStatus.label} tone={meetingStatus.tone} />
              <h1 className="text-xl md:text-2xl font-black uppercase tracking-tight text-slate-800 truncate">
                {meeting.title}
              </h1>
            </div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-1">
              {meeting.meeting_date} · {normalizeInputTime(meeting.start_time || draft.start_time)} – {normalizeInputTime(meeting.end_time || draft.end_time)}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(`/sistema/agenda/${meeting.id}/editar`)}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-[#51A7FD] flex items-center gap-2 transition-colors"
          >
            <Pencil size={14} />
            {isProcessed ? 'Reabrir y editar' : 'Editar reunion'}
          </button>
          <button
            type="button"
            onClick={() => setShowAttachments((v) => !v)}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-[#51A7FD] flex items-center gap-2 transition-colors"
          >
            <Paperclip size={14} />
            Adjuntos
          </button>
          {!isProcessed && (
            <button
              type="button"
              onClick={handleFinalizeMeeting}
              disabled={finalizing || diff < 0}
              className="rounded-xl bg-[#3FF2AA] text-[#0B2A36] px-4 py-2.5 text-[10px] font-black uppercase tracking-widest disabled:opacity-50 flex items-center gap-2 transition-opacity"
            >
              {finalizing ? <RefreshCcw size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Cerrar y procesar
            </button>
          )}
        </div>
      </div>

      {/* ── KPI Cards ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <KpiCard
          label="Duración planificada"
          value={formatMinutes(plannedMinutes)}
          sub={`${plannedMinutes} minutos previstos`}
        />
        <KpiCard
          label={isProcessed ? 'Duración real' : 'Duración actual'}
          value={formatMinutes(isProcessed ? realMinutes : scheduledMinutes)}
          sub={isProcessed
            ? `+${realMinutes - plannedMinutes}min sobre lo previsto`
            : `${normalizeInputTime(draft.start_time)} – ${normalizeInputTime(draft.end_time)}`}
          highlight={isProcessed && realMinutes > plannedMinutes ? 'amber' : undefined}
        />
        <KpiCard
          label="Pendiente de distribuir"
          value={diff === 0 ? 'Completo' : `${diff > 0 ? '+' : ''}${formatMinutes(Math.abs(diff))}`}
          sub={`${totalAssigned}min asignados de ${target}min totales`}
          highlight={diff === 0 ? 'green' : diff > 0 ? 'amber' : 'rose'}
        />
      </div>

      {/* ── Tiempos de la reunion (inline, compacto) ───────────────────────── */}
      {!isProcessed && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-5">
          <PanelTitle icon={Clock3} title="Horario de la reunion" subtitle="Ajusta inicio y cierre para recalcular la duración real" />
          <div className="p-4 flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[120px]">
              <CrmFieldLabel label="Inicio" tooltip={MEETING_DETAIL_TOOLTIPS.start_time} labelClassName="text-[8px] ml-0" />
              <input
                type="time"
                value={draft.start_time}
                onChange={(e) => handleMeetingDraftChange('start_time', e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-[11px] font-black outline-none focus:border-[#51A7FD]"
              />
            </div>
            <div className="flex-1 min-w-[120px]">
              <CrmFieldLabel label="Finalización" tooltip={MEETING_DETAIL_TOOLTIPS.end_time} labelClassName="text-[8px] ml-0" />
              <input
                type="time"
                value={draft.end_time}
                onChange={(e) => handleMeetingDraftChange('end_time', e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-[11px] font-black outline-none focus:border-[#51A7FD]"
              />
            </div>
            <div className="flex-1 min-w-[120px]">
              <CrmFieldLabel label="Total calculado" tooltip={MEETING_DETAIL_TOOLTIPS.total} labelClassName="text-[8px] ml-0" />
              <div className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-[11px] font-black text-slate-700 flex items-center gap-2">
                <Clock3 size={13} className="text-slate-400" />
                {formatMinutes(scheduledMinutes)}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap items-center pb-0.5">
              <Badge label={`Previsto ${formatMinutes(plannedMinutes)}`} tone="amber" />
              <Badge label={`${(meeting.participants || []).length} participantes`} tone="slate" />
            </div>
          </div>
        </div>
      )}

      {/* ── Main grid: temas + sidebar ─────────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">

        {/* ── LEFT: distribución de tiempo ────────────────────────────────── */}
        <div className="xl:col-span-2 space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <PanelTitle
              icon={CalendarRange}
              title="Distribución de tiempo por tema"
              subtitle="Cada tema tratado imputa minutos al proyecto correspondiente"
              right={
                !isProcessed && (
                  <button
                    type="button"
                    onClick={autoBalance}
                    className="flex items-center gap-1.5 rounded-xl border border-white/20 bg-white/10 px-3 py-1.5 text-[9px] font-black uppercase tracking-widest text-white hover:bg-white/20 transition-colors"
                  >
                    <Zap size={11} />
                    Repartir parejo
                  </button>
                )
              }
            />

            <div className="p-4 space-y-4">
              {/* ── Barra visual apilada ──────────────────────────────────── */}
              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">
                  Tiempo total ({formatMinutes(target)})
                </p>
                <div className="flex h-8 rounded-xl overflow-hidden border border-slate-200 bg-slate-100">
                  {(draft.topics || []).map((t, idx) => {
                    const mins = parseInt(t.topic_minutes, 10) || 0;
                    if (!mins || !t.is_treated) return null;
                    const w = (mins / target) * 100;
                    const color = TOPIC_COLORS[idx % TOPIC_COLORS.length];
                    const topicRow = (meeting.topics || []).find((row) => row.id === t.id);
                    return (
                      <div
                        key={t.id}
                        style={{ width: `${w}%`, background: color }}
                        className="grid place-items-center transition-all"
                        title={`${topicRow?.title || 'Tema'} · ${mins}min`}
                      >
                        {w > 8 && (
                          <span className="text-white text-[9px] font-black">{mins}m</span>
                        )}
                      </div>
                    );
                  })}
                  {diff > 0 && target > 0 && (
                    <div
                      className="flex-1 grid place-items-center text-[9px] font-bold text-slate-400"
                      style={{ width: `${(diff / target) * 100}%` }}
                    >
                      {diff > target * 0.12 && 'sin asignar'}
                    </div>
                  )}
                </div>
                {/* Leyenda */}
                <div className="flex flex-wrap gap-2 mt-2">
                  {(draft.topics || []).map((t, idx) => {
                    if (!t.is_treated) return null;
                    const topicRow = (meeting.topics || []).find((row) => row.id === t.id);
                    return (
                      <div key={t.id} className="flex items-center gap-1">
                        <span
                          className="w-2.5 h-2.5 rounded-sm shrink-0"
                          style={{ background: TOPIC_COLORS[idx % TOPIC_COLORS.length] }}
                        />
                        <span className="text-[9px] font-bold text-slate-500 truncate max-w-[100px]">
                          {topicRow?.title || `Tema ${idx + 1}`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ── Tarjetas de tema ──────────────────────────────────────── */}
              <div className="space-y-3">
                {(meeting.topics || []).length === 0 ? (
                  <p className="text-[10px] font-bold text-slate-400 py-4 text-center">
                    Sin temas cargados para esta reunion.
                  </p>
                ) : (
                  (meeting.topics || []).map((topic, idx) => {
                    const topicDraft = (draft.topics || []).find((row) => row.id === topic.id) || {
                      id: topic.id,
                      project_id: topic.project_id || '',
                      is_treated: Boolean(topic.is_treated),
                      topic_minutes: topic.topic_minutes == null ? '' : String(topic.topic_minutes),
                      topic_participant_ids: [],
                    };
                    const ticketRow = ticketMap[topic.ticket_id] || null;
                    const projectRow = projectMap[topic.project_id || ticketRow?.project_id] || null;
                    const clientRow = clientMap[projectRow?.client_id || ticketRow?.client_id] || null;
                    const accentColor = TOPIC_COLORS[idx % TOPIC_COLORS.length];
                    const topicMinsNum = parseInt(topicDraft.topic_minutes, 10) || 0;
                    const pct = target > 0 ? Math.round((topicMinsNum / target) * 100) : 0;

                    return (
                      <div
                        key={topic.id}
                        className={`rounded-xl border transition-all ${topicDraft.is_treated ? 'border-slate-200 bg-white' : 'border-dashed border-slate-200 bg-slate-50/60'}`}
                      >
                        {/* Franja color */}
                        <div
                          className="h-1 rounded-t-xl"
                          style={{ background: topicDraft.is_treated ? accentColor : '#E2E8F0' }}
                        />

                        <div className="p-3 grid grid-cols-12 gap-3 items-center">
                          {/* Proyecto + titulo */}
                          <div className="col-span-12 sm:col-span-5">
                            <div className="flex items-start gap-2">
                              {/* Tratado checkbox */}
                              {isProcessed ? (
                                <Badge
                                  label={topic.is_treated ? 'Tratado' : 'No tratado'}
                                  tone={topic.is_treated ? 'emerald' : 'slate'}
                                />
                              ) : (
                                <label className="inline-flex items-center gap-1.5 mt-0.5 cursor-pointer shrink-0">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(topicDraft.is_treated)}
                                    onChange={(e) =>
                                      handleTopicDraftChange(topic.id, 'is_treated', e.target.checked)
                                    }
                                    className="accent-[#51A7FD] w-3.5 h-3.5"
                                    title={MEETING_DETAIL_TOOLTIPS.treated}
                                  />
                                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                                    Tratado
                                  </span>
                                </label>
                              )}
                            </div>
                            <p className="text-[11px] font-black uppercase text-slate-800 mt-1.5">
                              {topic.title}
                            </p>
                            <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mt-0.5">
                              {projectRow
                                ? `${projectRow.code ? `#${projectRow.code} · ` : ''}${projectRow.name}`
                                : ticketRow
                                ? `#${ticketRow.ticket_number} ${ticketRow.title}`
                                : 'Sin proyecto'}
                              {clientRow ? ` · ${clientRow.full_name}` : ''}
                            </p>
                            {topic.notes && (
                              <p className="text-[10px] text-slate-500 mt-1 line-clamp-2">
                                {topic.notes}
                              </p>
                            )}
                          </div>

                          {/* Slider */}
                          <div className="col-span-12 sm:col-span-4">
                            {isProcessed ? (
                              <div className="flex items-center gap-2">
                                <Badge label={formatMinutes(topic.topic_minutes)} tone="amber" />
                                {target > 0 && topic.topic_minutes && (
                                  <span className="text-[10px] font-black text-slate-400 tabular-nums">
                                    {Math.round((topic.topic_minutes / target) * 100)}%
                                  </span>
                                )}
                              </div>
                            ) : (
                              <>
                                <input
                                  type="range"
                                  min={0}
                                  max={target || 120}
                                  value={topicMinsNum}
                                  onChange={(e) =>
                                    handleTopicDraftChange(
                                      topic.id,
                                      'topic_minutes',
                                      e.target.value === '0' ? '' : e.target.value,
                                    )
                                  }
                                  className="w-full"
                                  style={{ accentColor }}
                                  title={MEETING_DETAIL_TOOLTIPS.topic_minutes}
                                />
                                <div className="flex justify-between text-[9px] text-slate-400 font-bold mt-0.5">
                                  <span>0</span>
                                  <span>{target || 120}m</span>
                                </div>
                              </>
                            )}
                          </div>

                          {/* Input numérico con +/- */}
                          {!isProcessed && (
                            <div className="col-span-8 sm:col-span-2 flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() =>
                                  handleTopicDraftChange(
                                    topic.id,
                                    'topic_minutes',
                                    topicMinsNum <= 5 ? '' : String(topicMinsNum - 5),
                                  )
                                }
                                className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 grid place-items-center transition-colors"
                              >
                                <Minus size={10} />
                              </button>
                              <input
                                type="number"
                                min="0"
                                max={target || 120}
                                value={topicDraft.topic_minutes}
                                onChange={(e) =>
                                  handleTopicDraftChange(topic.id, 'topic_minutes', e.target.value)
                                }
                                className="flex-1 min-w-0 text-center bg-white border border-slate-200 rounded-xl px-1 py-2 text-[11px] font-black outline-none focus:border-[#51A7FD]"
                                placeholder="Auto"
                                title={MEETING_DETAIL_TOOLTIPS.topic_minutes}
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  handleTopicDraftChange(
                                    topic.id,
                                    'topic_minutes',
                                    String(Math.min(target || 120, topicMinsNum + 5)),
                                  )
                                }
                                className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 grid place-items-center transition-colors"
                              >
                                <Plus size={10} />
                              </button>
                            </div>
                          )}

                          {/* Porcentaje */}
                          {!isProcessed && (
                            <div className="col-span-4 sm:col-span-1 text-right">
                              <span
                                className="text-[12px] font-black tabular-nums"
                                style={{ color: topicMinsNum > 0 ? accentColor : '#94A3B8' }}
                              >
                                {pct}%
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Participantes extra (solo edición) */}
                        {!isProcessed && (options.profiles || []).length > 0 && (
                          <div className="px-3 pb-3">
                            <p className="text-[8px] font-black uppercase tracking-widest text-slate-400 mb-1.5">
                              Solo para este tema
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {(options.profiles || []).map((user) => {
                                const checked = (topicDraft.topic_participant_ids || []).includes(user.id);
                                return (
                                  <label
                                    key={`${topic.id}-${user.id}`}
                                    className={`flex items-center gap-1.5 rounded-xl border px-2.5 py-1.5 cursor-pointer transition-all ${checked ? 'bg-[#F2F9FF] border-[#51A7FD]/40 text-[#51A7FD]' : 'bg-white border-slate-200 text-slate-600'}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => handleToggleTopicParticipant(topic.id, user.id)}
                                      className="sr-only"
                                    />
                                    <span className="text-[9px] font-black uppercase tracking-widest truncate">
                                      {user.full_name || user.email}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Vista procesada: participantes extra */}
                        {isProcessed && (topic.topic_participants || []).length > 0 && (
                          <div className="px-3 pb-3 flex flex-wrap gap-1.5">
                            {topic.topic_participants.map((p) => (
                              <Badge
                                key={p.id}
                                label={userMap[p.profile_id]?.full_name || userMap[p.profile_id]?.email || 'Usuario'}
                                tone="rose"
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT: sidebar ──────────────────────────────────────────────── */}
        <div className="space-y-4">

          {/* Participantes */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <PanelTitle
              icon={Users}
              title="Participantes"
              subtitle={`${(meeting.participants || []).length} asistentes`}
            />
            <div className="p-4 space-y-2">
              {(meeting.participants || []).length === 0 ? (
                <p className="text-[10px] font-bold text-slate-400">Sin participantes registrados.</p>
              ) : (
                (meeting.participants || []).map((participant) => {
                  const u = userMap[participant.profile_id];
                  return (
                    <div
                      key={participant.id}
                      className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-xl bg-[#0B2A36] flex items-center justify-center shrink-0">
                        <span className="text-[10px] font-black text-[#3FF2AA]">
                          {(u?.full_name || u?.email || '?').slice(0, 1).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-black text-slate-800 truncate">
                          {u?.full_name || u?.email || 'Usuario'}
                        </p>
                        <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">
                          {u?.role || 'Equipo'}
                        </p>
                      </div>
                      <span className="text-[10px] font-black tabular-nums text-slate-400">
                        {formatMinutes(isProcessed ? realMinutes : scheduledMinutes)}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Cómo se imputará */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <PanelTitle icon={Info} title="Cómo se imputará" />
            <div className="p-4 space-y-3">
              {[
                `Cada participante recibe los ${formatMinutes(isProcessed ? realMinutes : scheduledMinutes)} distribuidos según los temas tratados.`,
                'Las horas se imputan al proyecto del tema. Los temas sin participantes propios usan los generales de la reunión.',
                'Los temas marcados como no tratados no generan imputación de horas.',
              ].map((text, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 size={13} className="text-[#3FF2AA] shrink-0 mt-0.5" />
                  <p className="text-[10px] text-slate-600 leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Horario procesado (read-only) */}
          {isProcessed && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <PanelTitle icon={Clock3} title="Horario registrado" subtitle="Resultado final procesado" />
              <div className="p-4 grid grid-cols-3 gap-3">
                <div>
                  <p className="text-[8px] font-black uppercase tracking-widest text-slate-400 mb-1">Inicio</p>
                  <p className="text-[13px] font-black text-slate-800">
                    {normalizeInputTime(meeting.start_time)}
                  </p>
                </div>
                <div>
                  <p className="text-[8px] font-black uppercase tracking-widest text-slate-400 mb-1">Cierre</p>
                  <p className="text-[13px] font-black text-slate-800">
                    {normalizeInputTime(meeting.end_time || draft.end_time)}
                  </p>
                </div>
                <div>
                  <p className="text-[8px] font-black uppercase tracking-widest text-slate-400 mb-1">Total</p>
                  <p className="text-[13px] font-black text-slate-800">
                    {formatMinutes(realMinutes)}
                  </p>
                </div>
                <div className="col-span-3 flex flex-wrap gap-1.5 mt-1">
                  <Badge label={`Previsto ${formatMinutes(plannedMinutes)}`} tone="amber" />
                  <Badge label={`Real ${formatMinutes(realMinutes)}`} tone="emerald" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Adjuntos ──────────────────────────────────────────────────────── */}
      {showAttachments && (
        <div className="mt-4">
          <AttachmentGallery
            organizationId={profile?.organization_id}
            entityType="meeting"
            entityId={meeting.id}
            currentUserId={profile?.id}
            title="Adjuntos de la reunion"
            subtitle="Actas, capturas y archivos de soporte"
            enabled
          />
        </div>
      )}
    </div>
  );
}
